<template>
  <div class="container pl-1 pr-1 pt-0">
    <div class="checkout-process" ref="checkout" id="checkout">
      <h1 class="title has-text-centered is-size-2 is-size-4-mobile">{{ $t('general.checkout') }}</h1>
      <div class="columns is-multiline">
        <div class="column is-12" v-if="inlinePaymentError">
          <div class="notification is-danger">
            {{ inlinePaymentError }}
          </div>
        </div>
        <div class="column is-12" v-if="notifications && notifications.length > 0">
          <div class="notification is-warning">
            <ul>
              <li v-for="notify in notifications" :key="notify.notification">
                <span v-if="notify.key">
                  {{ $t(`notifications.order.${notify.key}`, notify.params) }}
                </span>
                <span v-if="!notify.key">
                  <!-- this may be removed after all migration is done -->
                  {{ notify.notification }}
                </span>
              </li>
            </ul>
          </div>
        </div>
        <div class="column is-12-touch is-6-desktop is-7-widescreen">
          <div v-if="itemCount <= 0" class="notification is-primary">{{ $t('wk-empty-notice') }}</div>

          <div class="box pr-1 pl-1" v-if="itemCount > 0">
            <b-steps v-if="itemCount > 0" class="checkout-steps" v-model="step" :animated="true" :has-navigation="false">
              <b-step-item :label="$t('general.Payment')" :clickable="false" icon="credit-card">
                <PaymentMethod @next-step="toCustomerData" @start-over="toPaymentMethod" />
              </b-step-item>
              <b-step-item :label="$t('general.data')" :clickable="false" icon="user">
                <CustomerData v-if="user" :customer="user" ref="customerData" @validated="customerDataValidated" />
                <div class="level mt2 is-mobile">
                  <div class="level-left">
                    <div class="level-item">
                      <b-button v-if="selectedPayment.paymentTypeId != 20" @click="toPaymentMethod" icon-left="chevron-left">{{ $t('general.payment-type') }}</b-button>
                    </div>
                  </div>
                  <div class="level-right">
                    <div class="level-item" @click="runValidation">
                      <b-button type="is-primary" name="overviewButton" :disabled="!isCustomerDataValid" @click="setUserData" icon-right="chevron-right">{{ $t('general.overview') }}</b-button>
                    </div>
                  </div>
                </div>
              </b-step-item>

              <b-step-item :label="$t('general.overview')" :clickable="false" icon="stream">
                <Overview :customer="user" :payment="selectedPayment" v-if="step == 2" @tos-changed="triggerPaymentButton" @back-to-customer="toCustomerData" @back-to-payment="toPaymentMethod" @back-to-insurance="toInsurance" />
                <div class="level  t2 mt-4 is-mobile">
                  <div class="level-left">
                    <div class="level-item">
                      <b-button @click="toCustomerData" icon-left="chevron-left">{{ $t('general.data') }}</b-button>
                    </div>
                  </div>
                  <div class="level-right">
                    <div class="level-item">
                      <b-button type="is-primary" name="buyButton" :disabled="!tosAccepted || itemCount < 1" @click="finishOrder">{{ $t('general.buy') }}</b-button>
                    </div>
                  </div>
                </div>
              </b-step-item>
            </b-steps>
          </div>
        </div>
        <div class="column is-12-touch is-6-desktop is-5-widescreen">
          <div v-if="!items || items.length <= 0" class="notification is-info is-size-5 has-text-centered u-p2 has-text-light has-text-weight-semibold" name="emptyCartBanner">
            <b-icon icon="shopping-cart" size="is-large"></b-icon>
            <br />{{ $t('notices.cart-empty') }}
          </div>
          <div v-if="items && items.length > 0" class="box has-background-grey-lighter checkout-cart">
            <h2 class="title is-size-4">{{ $t('general.your-cart') }}</h2>
            <Cart :items="items" :currency="currency" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {mapGetters} from 'vuex'
import Cart from './Cart'
import CustomerData from './CustomerData'
import PaymentMethod from './PaymentMethod'
import Overview from './Overview'
//import Insurance from './_components/Insurance'
import StepsEnum from '../_constants/checkout-steps'
import OrderValidator from '@/api/order-validator'
//import { createInterface } from 'readline';
import anime from 'animejs'

export default {
  name: 'CheckoutProcess',
  components: {
    CustomerData,
    Cart,
    PaymentMethod,
    Overview,
    //Insurance,
  },
  computed: {
    ...mapGetters({
      user: '$_checkout/user',
      notifications: '$_checkout/notifications',
      items: 'productsInCart',
      selectedPayment: 'cartPaymentType',
      key: 'currentOrderKey',
      id: 'currentOrderId',
      currency: 'cartCurrency',
      session: '$_checkout/session',
      itemCount: 'itemCount',
    }),
  },
  data: function() {
    return {
      isCustomerDataValid: false,
      tosAccepted: false,
      step: 0,
      inlinePaymentError: null,
    }
  },
  async mounted() {
    this.$scrollTo('#checkout', 500, {
      offset: -160,
    })

    this.step = 0
    if (this.selectedPayment && this.selectedPayment.paymentTypeId === 20) {
      this.step = 1
    }
    await this.$store.dispatch('prepareForCheckout')
    await this.$store.dispatch('refreshCart', {delegateCustomer: true})
    this.$store.subscribe(mutation => {
      if (mutation.type == '$_checkout/ORDER_CLOSED') {
        const redirect = {
          id: mutation.payload.id,
          key: mutation.payload.token,
        }
        this.$router.push({name: 'order-complete', params: redirect})

        if (!this.$whitelabel.awinId) {
          this.$store.dispatch('clearCart')
        }
      }
    })
    this.$store.dispatch('$_checkout/checkOrderNotifications')

    if (this.$route.query && this.$route.query.pcb) {
      switch (this.$route.query.pcb) {
        case 'ProcessingFailure':
        case 'TransactionTimedOut':
          this.inlinePaymentError = this.$t('errors.payment-not-signed-in')
          break
        default:
          this.inlinePaymentError = this.$t('errors.payment-try-different')
          break
      }
    }

    if (this.$route.query && this.$route.query.amazonCheckoutSessionId) {
      let res = await this.$store.dispatch('$_checkout/paymentSessionReceived', {
        session: this.$route.query.amazonCheckoutSessionId,
        provider: 'amazon',
      })
      if (res) {
        this.step = 2
      }
    } else if (this.session && this.session.session && this.session.provider == 'amazon') {
      let res = await this.$store.dispatch('$_checkout/authorizeAmazonCheckoutSession', {
        session: this.session.session,
        provider: 'amazon',
      })
      if (res) {
        this.step = 2
      }
    }
  },
  methods: {
    async finishOrder() {
      let res = await OrderValidator.validateOrder()
      if (res.validForSale) {
        this.$store.dispatch('$_checkout/closeOrder')

        //let timeline = this.$anime.timeline()
        let timeline = anime.timeline()
        let checkoutElement = this.$refs.checkout

        timeline
          .add({
            targets: checkoutElement,
            opacity: 0,
            translateX: 300,
            duration: 1000,
            easing: 'easeInOutExpo',
          })
          .add({
            targets: this.$refs.checkoutFinished,
            opacity: 1,
            translateY: [60, 0],
            duration: 300,
            easing: 'easeOutQuad',
          })
      } else {
        OrderValidator.invalidOrderHandler(res)
      }
    },
    setUserData() {
      this.$store.dispatch('$_checkout/setUserData', this.user)
      this.step = StepsEnum.overview
    },
    async runValidation() {
      if (!this.isCustomerDataValid) {
        await this.$refs.customerData.runValidation()
      }
    },
    setWireTransferPaymentMethod() {
      this.$store.dispatch('$_checkout/setPaymentMethodWireTransfer')
    },
    toPaymentMethod() {
      this.step = StepsEnum.payment
      this.scrollToTop()
    },
    toInsurance() {
      this.step = StepsEnum.insurance
      this.scrollToTop()
    },
    toCustomerData() {
      this.step = StepsEnum.customer
      this.scrollToTop()
    },
    goToSearch() {
      this.$router.push({name: 'search'})
    },
    customerDataValidated(val) {
      this.isCustomerDataValid = val
    },
    triggerPaymentButton(val) {
      this.tosAccepted = val
    },
    scrollToTop() {
      setTimeout(() => {
        this.$scrollTo('.checkout-steps', 500, {
          offset: -200,
        })
      }, 400)
    },
  },
}
</script>

<style lang="scss">
.steps {
  margin-bottom: 1rem;
}

.checkout-cart {
  position: sticky;
  top: 2rem;
}

.checkout-finished {
  opacity: 0;
  //transform: translateY(5em);
  position: absolute;
  top: 0;
  width: 100%;
}

@media screen and(max-width:480px) {
  .checkout-steps {
    .step-title {
      font-size: 0.9rem !important;
    }
  }
}
</style>
